import { useContext, useState } from 'react';
import { AuthFrameEmbedContext } from '@/components/AuthFrame';

/**
 * What the signed-out pages share — sign-in, sign-up, forgot and reset password, email
 * confirmation and the invitation page (I-0653).
 *
 * These are the pages somebody reaches first, very often from a link opened on a phone, so every
 * control on them is at least 44 × 44 px below the `sm` breakpoint. From `sm` up they keep the
 * compact desktop sizes the rest of the app uses.
 */

const LINK_TARGET =
  'inline-flex min-h-touch min-w-touch items-center justify-center rounded px-1 -mx-1 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:min-h-0 sm:min-w-0';

/**
 * A text link in a sentence such as "Don't have an account? Create one". On a phone the link's box
 * grows to 44 px around the words; the words themselves do not move.
 */
export const signedOutLinkClasses = `${LINK_TARGET} font-medium text-gray-700 hover:text-gray-950 dark:text-gray-200 dark:hover:text-white`;

/** The quieter link under the password field ("Forgot password?"). */
export const signedOutQuietLinkClasses = `${LINK_TARGET} text-[12px] font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100`;

/**
 * Whether the first field of a form should take focus on arrival.
 *
 * Only where a keyboard is the main input (I-0653 AC-3). On a touch screen autofocus raises no
 * keyboard on iOS and helps nobody, yet it puts the first field into its focus style before the
 * person has touched anything — so a fresh sign-in form showed a white, ringed email field above a
 * grey password field, and read as two different kinds of field. With a mouse and keyboard, landing
 * in the field is what lets somebody type straight away, and the focus style is telling the truth.
 *
 * Where the question cannot be asked (no `matchMedia`), the answer is yes — the behaviour the pages
 * had before.
 */
export function autoFocusFirstField(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(pointer: fine)').matches;
}

/**
 * `autoFocusFirstField`, read once, for a page that may be drawn inside another frame. F-0581 OB-9:
 * inside Get started's step 2 the step's heading holds the focus a step change gives it, and
 * announces "Step 2 of 6"; the embedded form's first field never takes it.
 *
 * *Mutation:* ignore the embedding → "the embedded sign-up form leaves focus on the step heading"
 * goes red.
 */
export function useAutoFocusFirstField(): boolean {
  const embedded = useContext(AuthFrameEmbedContext);
  const [wanted] = useState(autoFocusFirstField);
  return wanted && !embedded;
}
